import { useState, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { Home, Sidebar, Breadcrumb } from "../components";
import {
  Navigation,
  ProductGroup,
  ProductGroupAdd,
  ProductGroupEdit,
  Products,
  ProductsEdit,
  ProductsAdd,
  Combos,
  ComboDetails,
  CombosAdd,
  FileApprovals,
  KeyPos,
  PosDecentralized,
  Survey,
  GeoTree,
  GeoTreeEdit,
  GeoTreeAdd,
  Ezds,
  EzdDetails,
  EzdBranchAdd,
  EzdBranchEdit,
  SelloutFileApprovals,
  ValidationRules,
  ValidationRulesEdit,
  SelloutFileSubmission,
} from "../containers";
import KeyPosEdit from "../containers/KeyPos/KeyPosDetails/KeyPosEdit";
import DecentralizedEdit from "../containers/PosDecentralized/PosDecentralizedDetails/DecentralizedEdit";
import Amplify from "@aws-amplify/core";
import Auth from "@aws-amplify/auth";
import { Hub } from "@aws-amplify/core";
import { actions as commonActions } from "../store/actions";
import { actions as sideBarActions } from "../containers/Sidebar/state/actions";
import { currentConfig } from "../auth/config/authConfig";
import { useDispatch } from "react-redux";
import { formattedUserGroups } from "../utils/utils";
import Spinner from "../components/Spinner/Spinner";
import SurveyEdit from "../containers/Survey/SurveyDetails/SurveyEdit";
import UnAuthorizedPage from "./../components/UnAuthorizedPage/UnAuthorizedPage";
import ProtectedRoutes from "../containers/ProtectedRoute/ProtectedRoutes";
import { useSelector } from "react-redux";
import "./styles.scss";
import Toaster from "../components/SnackBar/Toast";

Amplify.configure(currentConfig);

const App = () => {
  const [toggleSidebar, settoggleSidebar] = useState(false);
  const handleSidebarOpen = () => settoggleSidebar(!toggleSidebar);
  const [activeSession, setActiveSession] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const commonState = useSelector((state) => state.common);
  const dispatch = useDispatch();

  useEffect(() => {
    Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "tokenRefresh":
          setActiveSession(false);
          getAuthSession();
          break;
        case "tokenRefresh_failure":
          console.log("token refresh failed");
          setActiveSession(false);
          Auth.federatedSignIn();
          break;
        case "cognitoHostedUI_failure":
          console.log("cognitoHostedUI_failure", data);
          break;
        default:
          console.log("event", event);
          break;
      }
    });
    getAuthSession();
  }, []);

  const groups = commonState.userInfo?.userGroups || [];

  useEffect(() => {
    if (groups.length > 0 && (commonState.isCountryChanged || commonState.isCountryAvailable)) {
      dispatch(commonActions.setLoading(true));
      dispatch(sideBarActions.getMenus({ imdlGroups: groups.join(",")?.replace(/ /g, ""), countrySk: commonState.userInfo?.countrySk  }));
    }
  }, [commonState.userInfo?.userGroups, commonState.userInfo?.countrySk, commonState.isCountryAvailable]);

  const getAuthSession = async () => {
    try {
      let userInfo;
      if (!activeSession) {
        const session = await Auth.currentSession();
        const user = await Auth.currentUserInfo();
        userInfo = {
          principalName:
            user?.attributes["custom:userprincipalname"]?.split("@")[0] || "",
          userGroups: user?.attributes["custom:group"]
            ? formattedUserGroups(user?.attributes["custom:group"])
            : "",
        };

        dispatch(commonActions.setUserInfo(userInfo));
        setIsAuthenticated(true);
        setActiveSession(true);
      }
    } catch (error) {
      console.log(error, error.message)
      setActiveSession(false);
      setIsAuthenticated(false);
      Auth.federatedSignIn();
    }
  };

  return (
    <div className="app">
      {isAuthenticated ? (
        <div className="app__main">
          {groups.length > 0 ? (
            <>
              <div className="app__left">
                <Sidebar toggleSidebarMenu={toggleSidebar} />
              </div>
              <div className="app__right">
                <Navigation handleSidebar={handleSidebarOpen} />
                <Breadcrumb />
                <Toaster />
                <Switch>
                  <Route path="/" exact render={() => <Home />} />
                  <Route
                    path="/productgroup/add"
                    render={() => <ProductGroupAdd />}
                  />
                  <Route
                    path="/productgroup/:id"
                    render={() => <ProductGroupEdit />}
                  />
                  <ProtectedRoutes
                    name="Product Group"
                    path="/productgroup"
                    component={ProductGroup}
                  />
                  <Route path="/products/add" exact render={() => <ProductsAdd />} />
                  <Route path="/products/:id" exact render={() => <ProductsEdit />} />
                  <ProtectedRoutes
                    name="Products"
                    path="/products"
                    component={Products}
                  />
                  <Route path="/combos/add" render={() => <CombosAdd />} />
                  <Route path="/combos/:id" component={ComboDetails} />
                  <ProtectedRoutes
                    name="Combos"
                    path="/combos"
                    component={Combos}
                  />
                  <ProtectedRoutes
                    name="File Approvals"
                    path="/approvals"
                    component={FileApprovals}
                  />
                  <Route path="/keypos/:id" render={() => <KeyPosEdit />} />
                  <ProtectedRoutes
                    name="Key Pos"
                    path="/keypos"
                    component={KeyPos}
                  />

                  <Route
                    path="/decentralized/:id"
                    render={() => <DecentralizedEdit />}
                  />
                  <ProtectedRoutes
                    name="Pos Decentralized"
                    path="/decentralized"
                    component={PosDecentralized}
                  />
                  <Route path="/survey/:id" render={() => <SurveyEdit />} />
                  <ProtectedRoutes
                    name="Pos Survey"
                    path="/survey"
                    component={Survey}
                  />
                  <Route path="/geotree/add" render={() => <GeoTreeAdd />} />
                  <Route path="/geotree/:id" render={() => <GeoTreeEdit />} />
                  <ProtectedRoutes
                    name="Geo Tree"
                    path="/geotree"
                    component={GeoTree}
                  />
                  <Route
                    path="/ezds/branch/add"
                    exact
                    component={EzdBranchAdd}
                  />
                  <Route path="/ezds/branch" component={EzdBranchEdit} />
                  <Route path="/ezds/:id" component={EzdDetails} />
                  <ProtectedRoutes name="Ezds" path="/ezds" component={Ezds} />

                  <ProtectedRoutes
                    name="Sellout File Approvals"
                    path="/sellout-file-approvals"
                    component={SelloutFileApprovals}
                  />

                  <Route path="/validation-rules/:id" exact render={() => <ValidationRulesEdit />} />
                  <ProtectedRoutes
                    name="Validation Rules"
                    path="/validation-rules"
                    component={ValidationRules}
                  />

                  <ProtectedRoutes
                    name="Sellout File Submission"
                    path="/sellout-file-submission"
                    component={SelloutFileSubmission}
                  />

                  <Redirect to="/" />
                </Switch>
              </div>
            </>
          ) : (
            <UnAuthorizedPage />
          )}
        </div>
      ) : (
        <div className="app__loading">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default App;
